import {
  compileSkyAuthoredRig,
  sampleSkyAuthoredPose,
  skyAuthoredPoseContact,
} from './115-sky-authored-pose.mjs';

const AXES = ['x', 'y', 'z'];
const point = (v) => Object.fromEntries(AXES.map((a, i) => [a, v[i]]));
const array = (p) => AXES.map((a) => p[a]);
const subtract = (a, b) => a.map((v, i) => v - b[i]);
const unit = (a) => {
  const n = Math.hypot(...a);
  return n > 1e-9 ? a.map((v) => v / n) : [0, 0, 1];
};
const dot = (a, b) => a.reduce((sum, v, i) => sum + v * b[i], 0);
const distance = (a, b) => Math.hypot(...subtract(array(a), array(b)));
const mul = (a, b) => [
  a[3] * b[0] + a[0] * b[3] + a[1] * b[2] - a[2] * b[1],
  a[3] * b[1] - a[0] * b[2] + a[1] * b[3] + a[2] * b[0],
  a[3] * b[2] + a[0] * b[1] - a[1] * b[0] + a[2] * b[3],
  a[3] * b[3] - a[0] * b[0] - a[1] * b[1] - a[2] * b[2],
];
function rotation(from, to) {
  const a = unit(from),
    b = unit(to),
    d = dot(a, b);
  if (d < -0.99999)
    return Math.abs(a[0]) < 0.8 ? [...unit([0, a[2], -a[1]]), 0] : [...unit([-a[2], 0, a[0]]), 0];
  const q = [
    a[1] * b[2] - a[2] * b[1],
    a[2] * b[0] - a[0] * b[2],
    a[0] * b[1] - a[1] * b[0],
    1 + d,
  ];
  const n = Math.hypot(...q);
  return q.map((v) => v / n);
}
function localDirection(matrix, v) {
  return [0, 4, 8].map((i) => matrix[i] * v[0] + matrix[i + 1] * v[1] + matrix[i + 2] * v[2]);
}
function normalAt(pose, node) {
  const m = pose.matrices[node];
  // The original Blender hinge +Y is its measured fitted panel normal.
  return unit([m[4], m[5], m[6]]);
}
function segmentDistance(p, a, b) {
  const d = subtract(array(b), array(a));
  const t = Math.max(
    0,
    Math.min(1, dot(subtract(array(p), array(a)), d) / Math.max(1e-9, dot(d, d))),
  );
  return distance(p, point(array(a).map((v, i) => v + d[i] * t)));
}

/** Install the original three-panel telescope assembly. Missing source sockets,
 * hinges or clips fail readiness; the two empty generated frames stay empty. */
export function installSkyPrismMirrorRig(state, source) {
  const rig = compileSkyAuthoredRig(source);
  if (
    !state ||
    rig?.worldId !== 'prism-mirrors' ||
    !['Idle', 'MirrorTurn', 'ReflectionRecoil'].every((name) =>
      rig.clips.some((c) => c.name === name),
    ) ||
    ![0, 1, 2].every(
      (i) =>
        rig.sockets.some((s) => s.name === `reflection_surface_${i}`) &&
        rig.nodes.some((n) => n.name === `mirror_hinge_${i}`),
    )
  )
    return false;
  state.authoredPropRigs ??= {};
  state.authoredPropRigs['prism-mirrors'] = rig;
  return true;
}

/** Two source-scale mirror hubs flank the arena. Player hits rotate actual
 * authored joints; source facets remain the collision surface in every pose. */
export function createSkyPrismWidow(state, boss) {
  const rig = state.authoredPropRigs?.['prism-mirrors'];
  if (boss.worldId !== 'prism-widow' || !rig) return false;
  boss.prism = {
    phase: 'approach',
    age: 0,
    beams: [],
    reflections: 0,
    severed: [],
    overrides: [],
    web: [],
    illusions: [-1, 1].map((side) => ({
      id: state.nextId++,
      kind: 'authored-prop',
      artVariant: 'prism-widow',
      prismIllusion: true,
      side,
      collisionEnabled: false,
    })),
  };
  boss.authoredProps = [-1, 1].map((side) => {
    const prop = {
      id: state.nextId++,
      kind: 'authored-prop',
      worldId: 'prism-mirrors',
      artVariant: 'prism-mirrors',
      authoredRig: rig,
      x: side * 26,
      y: 22,
      z: -30,
      hp: 1,
      maxHp: 1,
      collisionEnabled: true,
      prismMirror: true,
      rootTransform: { position: [side * 26, 22, -30], quaternion: [0, 0, 0, 1], scale: [1, 1, 1] },
      panels: [0, 1, 2].map((i) => ({
        index: i,
        node: rig.nodes.findIndex((n) => n.name === `mirror_hinge_${i}`),
        aligned: false,
        turn: 0,
        cooldown: 0,
      })),
    };
    poseMirror(prop, boss, 0);
    return prop;
  });
  return true;
}
function rotateVector(q, v) {
  return mul(mul(q, [...v, 0]), [-q[0], -q[1], -q[2], q[3]]).slice(0, 3);
}
function worldPoint(m, p) {
  return point([0, 1, 2].map((i) => m[i] * p[0] + m[i + 4] * p[1] + m[i + 8] * p[2] + m[i + 12]));
}
function poseMirror(prop, boss, dt) {
  prop.restPose ??= sampleSkyAuthoredPose(prop.authoredRig, 'Idle', 0, prop.rootTransform);
  const restPose = prop.restPose,
    overrides = [];
  for (const panel of prop.panels) {
    panel.cooldown = Math.max(0, panel.cooldown - dt);
    panel.turn = Math.min(1, Math.max(0, panel.turn + (panel.aligned ? dt / 1.6 : -dt / 1.6)));
    const rest = restPose.nodes[panel.node],
      parent = prop.authoredRig.nodes[panel.node].parent;
    const parentMatrix = restPose.matrices[parent];
    const socket =
      prop.authoredRig.nodes[
        prop.authoredRig.sockets.find((v) => v.name === `reflection_surface_${panel.index}`).node
      ];
    let surface = restPose.sockets[`reflection_surface_${panel.index}`],
      orientation = rest.quaternion;
    // Solve only six small joint transforms; skin the protected source facets
    // once after all bearings. Iterative skin sampling here multiplied CPU cost.
    for (let solve = 0; solve < 3; solve++) {
      const incoming = unit(subtract(array(surface), array(boss.attackSocket)));
      const outgoing = unit(
        subtract(array(boss.authoredPose.sockets.abdomen_core), array(surface)),
      );
      const wanted = unit(subtract(incoming, outgoing));
      const current = normalAt(restPose, panel.node);
      if (dot(wanted, current) < 0)
        wanted.forEach((v, i) => {
          wanted[i] = -v;
        });
      const q = mul(
        rotation(localDirection(parentMatrix, current), localDirection(parentMatrix, wanted)),
        rest.quaternion,
      );
      let blend = dot(rest.quaternion, q) < 0 ? q.map((v) => -v) : q;
      blend = rest.quaternion.map((v, i) => v * (1 - panel.turn) + blend[i] * panel.turn);
      const n = Math.hypot(...blend);
      orientation = blend.map((v) => v / n);
      const rotated = rotateVector(orientation, socket.position);
      surface = worldPoint(
        parentMatrix,
        rotated.map((v, i) => v + rest.position[i]),
      );
    }
    overrides.push({ node: panel.node, ...rest, quaternion: orientation });
  }
  const pose = sampleSkyAuthoredPose(prop.authoredRig, 'Idle', 0, prop.rootTransform, {
    overrides,
  });
  for (const panel of prop.panels) {
    panel.surface = pose.sockets[`reflection_surface_${panel.index}`];
    panel.normal = normalAt(pose, panel.node);
  }
  prop.authoredPose = pose;
  prop.weakpoints = prop.panels.map((p) => ({
    ...p.surface,
    id: `reflection_surface_${p.index}`,
    radius: 2.6,
    criticalRadius: 0,
    exposed: true,
  }));
  prop.weakpoint = prop.weakpoints[0];
  prop.body = { x: prop.x, y: prop.y + 4, z: pose.bounds.max[2], radius: 15 };
}

/** Beam geometry is derived from the source panel's fitted optical normal.
 * Both incident and reflected rays must hit real triangles. No socket hit or
 * timer can damage the abdomen through an intervening body surface. */
export function traceSkyPrismReflection(boss, prop, panel) {
  const from = boss.attackSocket,
    intended = panel.surface;
  const direction = unit(subtract(array(intended), array(from)));
  const beyond = point(array(intended).map((v, i) => v + direction[i] * 2));
  const contact = skyAuthoredPoseContact(prop.authoredPose, from, beyond);
  if (
    !contact ||
    distance(contact, intended) > 2.8 ||
    !prop.authoredRig.meshes[contact.meshIndex].name.includes('protected')
  )
    return { from, to: contact ?? beyond, reflected: null, core: false };
  const reflectedDirection = direction.map(
    (v, i) => v - 2 * dot(direction, panel.normal) * panel.normal[i],
  );
  const start = point(array(contact).map((v, i) => v + reflectedDirection[i] * 0.06));
  const end = point(array(start).map((v, i) => v + reflectedDirection[i] * 150));
  const hit = skyAuthoredPoseContact(boss.authoredPose, start, end);
  const core = Boolean(hit && distance(hit, boss.authoredPose.sockets.abdomen_core) < 5);
  return { from, to: contact, reflected: { from: start, to: hit ?? end }, core };
}
function fracture(boss) {
  const systems = boss.prism,
    leg = [2, 6, 0, 4][(systems.reflections - 1) % 4];
  const node = boss.authoredRig.nodes.findIndex((n) => n.name === `leg_0${leg}_2`);
  if (node < 0 || systems.overrides.some((v) => v.node === node)) return;
  const frame = structuredClone(boss.authoredPose.nodes[node]);
  // Distal crystal telescopes into the broken joint; physical triangles shorten
  // with the rendered limb. This persistent damage survives subsequent clips.
  frame.scale = frame.scale.map((v) => v * 0.5);
  systems.overrides.push({ node, ...frame });
}
function enter(boss, phase, event, state) {
  boss.prism.phase = phase;
  boss.prism.age = 0;
  boss.authoredTransition = boss.authoredPose;
  event(
    state,
    'prism-phase',
    phase === 'charge'
      ? 'LEGS BRACING · REFLECT THE BEAM'
      : phase === 'recoil'
        ? 'REFLECTED HIT · CRYSTAL LEG FRACTURED'
        : 'SHOOT MIRRORS TO ROTATE · CUT HUB ANCHORS',
  );
}

/** Deterministic signature clock. Charge visibly braces the source legs;
 * reflection damage shortens a real leg and cut anchors change movement routes.
 * Returns the authored clip used by116; the post-pose pass owns ray contacts. */
export function stepSkyPrismWidow(state, dt, { event }) {
  const boss = state.boss,
    s = boss.prism;
  boss.authoredFlightCeiling = Math.max(boss.authoredFlightCeiling, 33);
  s.age += dt;
  const duration = { approach: 4, weave: 4, charge: 1.4166667, beam: 2, recoil: 1.2083334 };
  if (s.age >= duration[s.phase])
    enter(
      boss,
      { approach: 'weave', weave: 'charge', charge: 'beam', beam: 'weave', recoil: 'weave' }[
        s.phase
      ],
      event,
      state,
    );
  boss.controlHint =
    s.phase === 'charge'
      ? 'BRACING · LINE UP A MIRROR'
      : s.phase === 'beam'
        ? 'BEAM LIVE · STAY CLEAR'
        : `ROTATE MIRRORS · ${s.reflections} REFLECTED HITS · ${s.severed.length}/6 ANCHORS CUT`;
  return {
    clip: {
      approach: 'Idle',
      weave: 'Crawl',
      charge: 'BeamCharge',
      beam: 'BeamSweep',
      recoil: 'MirrorRecoil',
    }[s.phase],
    seconds: s.age,
    target: 'abdomen_core',
    exposed: false,
  };
}

/** Recompute source mirror poses and visible beam/web segments after the boss
 * pose, avoiding a renderer-only reflection or a frame-late damage surface. */
export function finishSkyPrismWidow(state, dt, { event, hit }) {
  const boss = state.boss,
    s = boss.prism;
  for (const illusion of s.illusions) {
    illusion.authoredRig = boss.authoredRig;
    illusion.authoredPose = boss.authoredPose;
    illusion.rootTransform = {
      ...boss.rootTransform,
      position: boss.rootTransform.position.map(
        (v, i) => v + (i === 0 ? illusion.side * 32 : i === 2 ? -16 : 3),
      ),
    };
  }
  for (const prop of boss.authoredProps) poseMirror(prop, boss, dt);
  s.web = boss.authoredProps.flatMap((prop, side) =>
    [0, 1, 2]
      .filter((i) => !s.severed.includes(side * 3 + i))
      .map((i) => ({
        id: side * 3 + i,
        from: prop.authoredPose.sockets[`hub_attachment_${i * 2}`],
        to: boss.authoredPose.sockets[`web_anchor_0${side * 4 + i}`],
      })),
  );
  s.beams = [];
  if (!['charge', 'beam'].includes(s.phase)) return;
  const options = boss.authoredProps.flatMap((prop) =>
    prop.panels.map((panel) => ({ prop, panel })),
  );
  const candidate =
    options.find(
      ({ prop, panel }) => panel.turn >= 0.999 && traceSkyPrismReflection(boss, prop, panel).core,
    ) ??
    options.find(({ panel }) => panel.turn >= 0.999) ??
    options[0];
  const beam = traceSkyPrismReflection(boss, candidate.prop, candidate.panel);
  s.beams.push({ ...beam, active: s.phase === 'beam' });
  if (s.phase !== 'beam') return;
  const pilot = { x: state.x, y: state.y, z: state.pilotZ ?? 1 };
  if (
    segmentDistance(pilot, beam.from, beam.to) < 0.8 ||
    (beam.reflected && segmentDistance(pilot, beam.reflected.from, beam.reflected.to) < 0.8)
  )
    hit?.(state, 'prism-beam');
  if (beam.core && candidate.panel.turn >= 0.999) {
    boss.hp = Math.max(0, boss.hp - boss.maxHp / 4);
    s.reflections++;
    fracture(boss);
    candidate.panel.aligned = false;
    enter(boss, 'recoil', event, state);
  }
}

/** Only manual nearest-triangle prop contacts command a rotation or cut an
 * anchor. Direct bullets and nova cannot bypass the reflected-beam encounter. */
export function resolveSkyPrismContact(state, target, shot, contact, { autonomous }, { event }) {
  const boss = state.boss;
  if (!boss?.prism) return undefined;
  if (target === boss) return { handled: true, damage: 0, quality: 'armor' };
  if (!target.prismMirror) return undefined;
  if (autonomous) return { handled: true, damage: 0, quality: 'armor' };
  const panel = target.panels.find((p) => distance(contact, p.surface) < 3.2);
  if (panel && panel.cooldown <= 0) {
    panel.aligned = true;
    panel.cooldown = 0.7;
    event(state, 'prism-mirror', 'MIRROR BEARING ROTATING · PREPARE REFLECTION');
  } else {
    const side = boss.authoredProps.indexOf(target);
    for (let i = 0; i < 3; i++) {
      const id = side * 3 + i;
      if (
        !boss.prism.severed.includes(id) &&
        distance(contact, target.authoredPose.sockets[`hub_attachment_${i * 2}`]) < 1.7
      ) {
        boss.prism.severed.push(id);
        event(state, 'prism-anchor', 'WEB ANCHOR CUT · SPIDER ROUTE CHANGED');
        break;
      }
    }
  }
  return { handled: true, damage: 0, quality: 'body' };
}
